import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTheme } from "styled-components";
import useMediaQuery from "../../hooks/useMediaQuerryHook";
import { useLocation } from "react-router-dom";
  
  const useDatePickerSlotProps = () => {
  // Infer the type of slotProps from the component props
  type SlotPropsType = React.ComponentProps<typeof DatePicker>["slotProps"];
  const defaultStyledTheme = useTheme();
  const isMobile = useMediaQuery(576);
  const location = useLocation();
  const dashboardLanding = location.pathname === "/dashboard/landing";

  const sharedPaperStyles = {
    backgroundColor: defaultStyledTheme.skillSetBg, // Background for the picker dialog

    "& .MuiTypography-root": {
      color: defaultStyledTheme.text, // General text color
      fontWeight: 600,
    },
    "& .MuiPickersCalendarHeader-label": {
      color: defaultStyledTheme.text, // Month/year display text
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      color: defaultStyledTheme.text, // Dropdown arrow for year selection
      "&:hover": {
        backgroundColor: defaultStyledTheme.ripplebg,
      },
    },
    "& .MuiPickersArrowSwitcher-button": {
      color: defaultStyledTheme.text, // Left and right navigation arrows
      "&:hover": {
        backgroundColor: defaultStyledTheme.ripplebg,
      },
    },
    "& .MuiPickersYear-root": {
      color: defaultStyledTheme.text, // Year text in the dropdown
      "& .MuiPickersYear-yearButton.Mui-selected": {
        //year selected button
        background: "linear-gradient( #FF00D6, #FF4D00) !important",
        color: "white !important",
        "&:hover": {
          background: `${defaultStyledTheme.yearsGradientDatesHover} !important`,
          color: `${defaultStyledTheme.text} !important`,
        },
      },
      "& .MuiPickersYear-yearButton": {
        color: defaultStyledTheme.text, // Default text color for unselected years
        "&:hover": {
          background: `${defaultStyledTheme.yearsGradientDatesHover} !important`, // Semi-transparent on hover for unselected years
          color: `${defaultStyledTheme.text} !important`,
        },
      },
    },
  };

  const DashboardMuiOutlinedInputRoot = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: defaultStyledTheme.searchInputBcg,
      "&:hover": {
        backgroundColor: defaultStyledTheme.searchInputBcgClrHver,
        "& .MuiOutlinedInput-input": {
          color: defaultStyledTheme.text, // Change text color on hover, even on the  openPickerButton space
        },
      },
      borderRadius: "25px",
      "&.Mui-focused": {
        backgroundColor: defaultStyledTheme.searchInputBcgClrHver,
      },
      //default outline settings
      "& fieldset": {
        borderColor: defaultStyledTheme.searchInputBdrClr,
        borderWidth: "1px",
        borderRadius: "25px",
      },
      "&:hover fieldset": {
        borderColor: defaultStyledTheme.searchInputBdrClrHver,
      },
      "&.Mui-focused fieldset": {
        borderColor: defaultStyledTheme.text,
      },
    },
  };

  const MuiOutlinedInputRoot = {
    "& .MuiOutlinedInput-root": {
      //background color for the input
      "&:hover": {
        backgroundColor: defaultStyledTheme.text,
        // color: defaultStyledTheme.circularIconTextBg,
        "& .MuiOutlinedInput-input": {
          color: defaultStyledTheme.circularIconTextBg, // Change text color on hover, even on the  openPickerButton space
        },
      },
      "&.Mui-focused": {
        backgroundColor: defaultStyledTheme.text,
      },
      //default outline settings
      "& fieldset": {
        borderColor: defaultStyledTheme.text,
        borderWidth: "2px",
        borderRadius: "2px",
      },
      "&:hover fieldset": {
        borderColor: defaultStyledTheme.text,
      },
      "&.Mui-focused fieldset": {
        borderColor: defaultStyledTheme.text,
      },
    },
  };

  const MuiOutlinedInputRootProps = dashboardLanding? DashboardMuiOutlinedInputRoot : MuiOutlinedInputRoot;

  const sharedSlotsProps: SlotPropsType = {
    textField: {
      size: "medium",
      // helperText: "will use this to display error messages",
      FormHelperTextProps: {
        sx: {
          color: "red",
        },
      },

      variant: "outlined",

      sx: {
        width: isMobile ? "100%" : "50%",
        ...MuiOutlinedInputRootProps,

        //date to be selected text color
        "& .MuiOutlinedInput-input": {
          fontSize: "14px",
          color: defaultStyledTheme.text,
          "&:focus": {
            color: defaultStyledTheme.circularIconTextBg,
          },
          "&:not(:focus)": {
            color: defaultStyledTheme.text, // Ensure color remains consistent when not focused
          },
        },
        // Special styling for when the picker is open
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
          color: defaultStyledTheme.circularIconTextBg, // Ensure consistent color when picker is open
        },

        //place holder color
        "& .MuiInputLabel-root": {
          color: "grey !important",
          "&.Mui-focused": {
            color: `${defaultStyledTheme.text} !important`,
            textShadow: defaultStyledTheme.placeHolderDateHover,
            fontSize: "16px",
            fontWeight: "bold",
          },
        },
      },
    },

    openPickerIcon: { fontSize: "small" }, //icon size

    // Change default icon color
    openPickerButton: {
      sx: {
        color: "grey",
        "&:hover": {
          color: defaultStyledTheme.circularIconTextBg, // Change color on hover
        },
      },
    },

    desktopPaper: {
      sx: sharedPaperStyles, //Use shared styles
    },

    mobilePaper: {
      sx: sharedPaperStyles, //Reuse shared styles
    },

    day: {
      sx: {
        //dates color
        color: defaultStyledTheme.text,
        // dates background color on hover
        "&:hover": {
          backgroundColor: defaultStyledTheme.datesHover,
        },
        "&.Mui-selected": {
          background: "linear-gradient( #FF00D6, #FF4D00) !important", // Background color for selected date
          color: "white !important", //force white text on selected date
          transition: "background 0.2s ease, color 0.2s ease",
          "&:hover": {
            background: `${defaultStyledTheme.gradientDatesHover} !important`,
            color: `${defaultStyledTheme.text} !important`,
          },
        },
        "&.Mui-selected:hover": {
          backgroundColor: `${defaultStyledTheme.ripplebg} !important`, // Background on hover for selected date
        },
        // Border color for today's date
        "&.MuiPickersDay-today": {
          borderColor: defaultStyledTheme.text,
          color: defaultStyledTheme.circularIconTextBg,
          "&:hover": {
            backgroundColor: defaultStyledTheme.ripplebg,
            color: defaultStyledTheme.text,
          },
          fontWeight: 600,
          borderWidth: "1px",
          borderStyle: "solid",
          backgroundColor: defaultStyledTheme.text,
        },
      },
    },

    actionBar: {
      sx: {
        "& .MuiButton-root": {
          color: defaultStyledTheme.text, // Default button text color
          fontWeight: "bold", // Optional: Make buttons bold
          "&:hover": {
            backgroundColor: defaultStyledTheme.ripplebg, // Optional: Change background on hover
          },
        },
      },
    },
  };

  return(
    sharedSlotsProps
  )

  }

  export default useDatePickerSlotProps;
  